import { Suspense, lazy, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Layout, Navbar, SubHeader } from "src/components";
import CraHomeScreen from "src/features/fields/pages/Homescreen";
import Loader from "./components/ui/loader.component";
import { isProd, logout } from "./constants/global-const";
import { accessControlActions } from "./context/access-control-slice";
import { authActions } from "./context/auth-slice";
import { RootState } from "./context/store";
import Login from "./pages/auth/login.component";
import BlockedRevoked from "./pages/blocked-counter";
import NotFound from "./pages/not-found/not-found.component";
import StockistDetails from "./pages/stockist-details";
import StockistMaster from "./pages/stockist-master/stockist-master.component";
import { fetchMenuAccessRoles } from "./services/access-control-api";
import { queryEmployeeDetails } from "./services/employee-api";
import CompanyMasterComponent from "./pages/company-master/company-master.component";
import RestrictedPage from "./container/restricted.component";
import OrdersHomePage from "./features/fields/pages/OrdersHomeScreen";
import OrdersDetailScreen from "./features/fields/pages/OrderDetailsTab/OrdersDetailScreen";
import OrdersProductScreen from "./features/fields/pages/OrdersDetail/OrdersProductScreen";
import OrderReviewScreen from "./features/fields/pages/OrdersDetail/OrderReviewScreen";
import OrderConfirmation from "./features/fields/pages/OrdersDetail/OrderConfirmation";
import InvoiceMasterComponent from "./pages/invoice-master/invoice-master.component";
import InvoiceHomepage from "./pages/invoice-homepage/Invoice-homepage";
import { InvoiceEntryScreen } from "./pages/invoice-details/InvoiceEntryScreen";
import OrderInfoScreen from "./features/fields/pages/OrderInfoTabs/OrderInfoScreen";
import { InvoiceApprovalScreen } from "./pages/invoice-details/InvoiceApprovalScreen";
import InvoiceComparison from "./pages/invoice-details/invoice-comparison.component";
import EInvoiceDetails from "./features/fields/pages/E-Invoices/E-InvoiceCards.tsx/E-InvoiceDetails";
import PoHomeScreen from "./features/fields/pages/PurchaseOrder/PoHomeScreen";
import PoBulk from "./features/fields/pages/PurchaseOrder/PoBulk";
import OrderDetailsReport from "./features/fields/pages/Reports/OrderDetailsReport";
import PoItems from "./features/fields/pages/PurchaseOrder/PoItems";
import OrderReports2 from "./features/fields/pages/Reports/OrderReports2";
import OrderProductsReport from "./features/fields/pages/Reports/OrderProductsReport";
import ReconciliationHomeScreen from "./features/fields/pages/Reconciliation";
import StockistBlock from "./features/fields/pages/StockistBlockRevoke/StockistBlock";
import OrderTypeException from "./features/fields/pages/OrderTypeException/OrderTypeException";
const CounterDetails = lazy(() => import("./pages/counter-details/counter-details.component"));
const DivisionDetails = lazy(() => import("./pages/division-details/division-details.component"));
const DivisionMaster = lazy(() => import("./pages/division-master/division-master.component"));
const ValueCap = lazy(() => import("./pages/value-cap"));

const EmployeeMaster = lazy(() => import("src/pages/employee-master/employee-master.component"));
const EmployeeDetails = lazy(() => import("src/pages/employees-details"));
const ProductDetails = lazy(() => import("src/pages/product-details"));
const ProductMaster = lazy(() => import("src/pages/product-master/product-master.component"));
const CounterMaster = lazy(() => import("src/pages/counter-master/counter-master.component"));
// const CraHomescreen = lazy(() => import("src/features/fields/pages/Homescreen"));
const PDAHomescreen = lazy(() => import("src/features/fields/pages/PDAHomescreen"));
const CraDetailScreen = lazy(() => import("src/features/fields/pages/DetailScreen"));
const PdaDetailScreen = lazy(() => import("src/features/fields/pages/productDetailsScreen"));
const GeographicMaster = lazy(() => import("src/pages/geography-master/geographic-master.component"));

export default function App(): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);

  const token: any = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    getMenuAccessRoles();
  }, []);

  const getMenuAccessRoles = async () => {
    try {
      const menuAccessRoles = await fetchMenuAccessRoles();
      dispatch(accessControlActions.fetchAccessControl(menuAccessRoles.data));
      setIsLoading(false);
    } catch (error) {
      console.error("Unable to fetch Menu Access Control Roles:", error);
    }
  };

  useEffect(() => {
    // const token: any = {
    //   token: {
    //     // sub: "55fc3d2f-f6c1-49f3-8b91-1ee91d60a971",
    //     sub: "729c1fcd-7285-48f8-91c8-1352fdad555f",
    //     "custom:role": "fieldforce",
    //   },
    // };

    if (token?.token && token?.token?.sub) {
      // const isAdmin = token?.token?.["custom:role"] === "admin" || token?.token?.["custom:role"] === "Medvol Field";
      const isAdmin = true;
      const query = isAdmin ? `/details?cognito_id=${token?.token?.sub}&cdh=true` : `/details?cognito_id=${token?.token?.sub}`;
      queryEmployeeDetails(query)
        .then((res) => {
          if (isAdmin) {
            dispatch(authActions.setCurrentPsr(res?.data?.[0] || []));
          } else {
            dispatch(authActions.setCurrentPsr(res?.data?.data?.[0] || []));
          }
        })
        .catch((err) => console.log(err));
    }
  }, [token?.token]);

  // useEffect(() => {
  //   const idTokenDetails = {
  //     "custom:role": "admin",
  //   };
  //   dispatch(authActions.setIdTokenDetails(idTokenDetails));
  // }, []);
  // logout after
  useEffect(() => {
    const currentTime = 120 * 60 * 1000; // 120 minutes = 2 hours
    // const currentTime = 1 * 60 * 1000; // 1 minutes

    const events = ["click", "load", "keypress"];
    let timer = setTimeout(() => logout(false), currentTime);

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => logout(false), currentTime);
    };

    events.forEach((event) => window.addEventListener(event, resetTimer));

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer));
      clearTimeout(timer);
    };
  }, []);

  // listen for logout in different tabs
  useEffect(() => {
    const channel = new BroadcastChannel("logout");
    channel.onmessage = () => {
      console.log("logout");
      logout(false, false);
    };

    return () => channel.close();
  }, []);

  const userRole: any = useSelector((state: RootState) => state.auth);
  const accessControl = useSelector((state: RootState) => state.accessControl);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  // useEffect(() => {
  //   // const getCookie = await getCookieValue("token");
  //   const setDataInRedux = async () => {
  //     const getIdToken = await getCookieValue("idToken");

  //     console.log("getCookie", getIdToken);

  //     if (getIdToken) {
  //       const decodedToken: string | null | undefined = await jwtDecode(getIdToken as string);
  //       let decodedName: any = null;
  //       if (getIdToken) {
  //         decodedName = await jwtDecode(getIdToken as string);
  //       }
  //       dispatch(authActions.login(decodedToken));
  //       dispatch(authActions.setLoggedInUser(decodedName?.name || ""));
  //       console.log("it is logged in");
  //     }
  //   };

  //   setDataInRedux();
  // }, [getCookieValue("idToken")]);

  return (
    <>
      {token.restricted && <RestrictedPage />}
      {!token.token && <Login />}
      {token.token && (!token.restricted) && (
        <Suspense fallback={<Loader />}>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Navigate to="masters" />} />
              <Route
                path="/masters"
                element={
                  <>
                    <Navbar />
                    <SubHeader />
                  </>
                }
              />

              {/* masters route starts here */}
              {accessControl.menus?.MASTERS?.ALLOWED_ROLES.includes(userRole.token["custom:mv_role"]) && (
                <>
                  <Route path="/Masters" element={<Layout />}>
                    {/* {products routes} */}
                    {accessControl.menus?.MASTERS?.SECONDARY_MENUS.PRODUCTS?.ALLOWED_ROLES.includes(userRole.token["custom:mv_role"]) && (
                      <>
                        <Route path="products" element={<ProductMaster />} />
                        <Route path="products/details" element={<ProductDetails />} />
                      </>
                    )}
                    {/* employees routes  */}
                    {accessControl.menus?.MASTERS?.SECONDARY_MENUS.EMPLOYEES?.ALLOWED_ROLES.includes(userRole.token["custom:mv_role"]) && (
                      <>
                        <Route path="employees" element={<EmployeeMaster />} />
                        <Route path="employees/details" element={<EmployeeDetails />} />
                      </>
                    )}
                    {/*counters routes*/}
                    {accessControl.menus?.MASTERS?.SECONDARY_MENUS.COUNTERS?.ALLOWED_ROLES.includes(userRole.token["custom:mv_role"]) && (
                      <>
                        <Route path="counters" element={<CounterMaster />} />
                        <Route path="counters/details" element={<CounterDetails />} />
                        <Route path="counters/details/value-cap" element={<ValueCap />} />
                        <Route path="counters/details/block-revoke" element={<BlockedRevoked />} />
                        <Route path="divisions" element={<DivisionMaster />} />
                        <Route path="divisions/details" element={<DivisionDetails />} />
                      </>
                    )}
                    {/* stockists route */}
                    {accessControl.menus?.MASTERS?.SECONDARY_MENUS.STOCKISTS?.ALLOWED_ROLES.includes(userRole.token["custom:mv_role"]) && (
                      <>
                        <Route path="stockists" element={<StockistMaster />} />
                        <Route path="stockists/details" element={<StockistDetails />} />
                      </>
                    )}
                    <Route path="geographic" element={<GeographicMaster />} />
                    {!isProd && <Route path="company" element={<CompanyMasterComponent />} />}
                    {!isProd && <Route path="invoice" element={<InvoiceMasterComponent />} />}
                  </Route>
                  {/* master route ends here */}
                </>
              )}
              {/* request approval routes starts here  */}

              <Route path="/request-approval" element={<Navigate to="/request-approval/cra" />} />
              <Route path="/request-approval" element={<Layout />}>
                <Route
                  path="cra"
                  element={
                    <>
                      <SubHeader />
                      <CraHomeScreen />
                    </>
                  }
                />
                <Route
                  path="cra/details"
                  element={
                    <>
                      <SubHeader />
                      <CraDetailScreen />
                    </>
                  }
                />

                <Route
                  path="pra/details"
                  element={
                    <>
                      <SubHeader />
                      <PdaDetailScreen />
                    </>
                  }
                />
                <Route
                  path="Pra"
                  element={
                    <>
                      <SubHeader />
                      <PDAHomescreen />
                    </>
                  }
                />
              </Route>
              {/* request approval routes ends here  */}

              {/* transactions routes starts here  */}
              {/* <Route
                path="/transactions"
                element={
                  <>
                    <Navbar />
                    <SubHeader />
                  </>
                }
              > */}
              <Route path="/transactions" element={<Navigate to="/transactions/orders" />} />
              <Route path="/transactions" element={<Layout />}>
                <Route
                  path="orders"
                  element={
                    <>
                      <SubHeader />
                      <OrdersHomePage />
                    </>
                  }
                />
                <Route
                  path="orders/:tabIndex"
                  element={
                    <>
                      <SubHeader />
                      <OrdersHomePage />
                    </>
                  }
                />
                <Route
                  path="/transactions/orders/details/:counter_code/:tabIndex"
                  element={
                    <>
                      <SubHeader />
                      <OrdersDetailScreen />
                    </>
                  }
                />
                <Route
                  path="/transactions/orders/info/:orderCode/:tabIndex"
                  element={
                    <>
                      <SubHeader />
                      <OrderInfoScreen />
                    </>
                  }
                />
                <Route
                  path="/transactions/orders/products/:locationCode"
                  element={
                    <>
                      <SubHeader />
                      <OrdersProductScreen />
                    </>
                  }
                />
                <Route
                  path="/transactions/orders/review/:locationCode"
                  element={
                    <>
                      <SubHeader />
                      <OrderReviewScreen />
                    </>
                  }
                />
                <Route
                  path="/transactions/orders/confirmation"
                  element={
                    <>
                      <SubHeader />
                      <OrderConfirmation />
                    </>
                  }
                />
                {/* <Route path="orders/home" element={<OrdersHomePage />} /> */}

                {/*Invoices routes start here */}
                <Route
                  path="invoice"
                  element={
                    <>
                      <InvoiceHomepage />
                    </>
                  }
                />
                <Route
                  path="invoice/:tabIndex"
                  element={
                    <>
                      <InvoiceHomepage />
                    </>
                  }
                />
                <Route
                  path="/transactions/invoice/:stockist_invoice_attachment_id/0"
                  element={
                    <>
                      <InvoiceEntryScreen />
                    </>
                  }
                />
                <Route
                  path="/transactions/invoice/:stockist_invoice_attachment_id/1"
                  element={
                    <>
                      <InvoiceApprovalScreen />
                    </>
                  }
                />
                <Route
                  path="/transactions/invoice/product-mapping/:stockist_invoice_attachment_id"
                  element={
                    <>
                      <InvoiceComparison />
                    </>
                  }
                />
                <Route
                  path="/transactions/e-invoice/:einvoice_dtl_id"
                  element={
                    <>
                      <EInvoiceDetails />
                    </>
                  }
                />
                {/* Purchase Order routes start here */}
                <Route
                  path="/transactions/purchase-order"
                  element={
                    <>
                      <PoHomeScreen />
                    </>
                  }
                />
                <Route
                  path="/transactions/purchase-order/:poBatchId"
                  element={
                    <>
                      <PoBulk />
                    </>
                  }
                />
                <Route
                  path="/transactions/purchase-order/:poBatchId/:po_id"
                  element={
                    <>
                      <PoItems />
                    </>
                  }
                />
                   <Route
                  path="/transactions/reconciliation"
                  element={
                    <>
                      <ReconciliationHomeScreen />
                    </>
                  }
                />
                <Route
                  path="/transactions/stockist-block-revoke"
                  element={
                    <>
                      <StockistBlock />
                    </>
                  }
                />
                <Route
                  path="/transactions/order-type-exception"
                  element={
                    <>
                      <OrderTypeException />
                    </>
                  }
                />
                {/* Purchase Order routes ends here */}
              </Route>
              {/* transactions routes ends here  */}

              {/*Reports routes starts here  */}
              <Route path="/reports" element={<Navigate to="/reports/orders-details" />} />
              <Route path="/reports" element={<Layout />}>
                {/* <Route
                  path="orders-superset"
                  element={
                    <>
                      <SubHeader />
                      <ReportsHomePage />
                    </>
                  }
                /> */}
                <Route
                  path="orders-details"
                  element={
                    <>
                      <SubHeader />
                      <OrderDetailsReport />
                    </>
                  }
                />
                <Route
                  path="orders-report"
                  element={
                    <>
                      <SubHeader />
                      <OrderReports2 />
                    </>
                  }
                />
                <Route
                  path="counter-product-report"
                  element={
                    <>
                      <SubHeader />
                      <OrderProductsReport />
                    </>
                  }
                />
                /</Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </Suspense>
      )}
    </>
  );
}
